import React from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const TermsOfService = () => (
  <div className="heritage-canvas">
    <SEO
      title="Terms of Service"
      description="Terms of Service for AfriMarket — the marketplace operated by AfriMarket Group Ltd."
      path="/terms"
    />
    <div className="container-narrow py-16 max-w-3xl">
      <div className="label-overline text-[#C05A3C] mb-3">Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-10">
        Last updated: 18 February 2026 · Effective immediately
      </p>

      <div className="prose-legal space-y-7 text-[15px] leading-relaxed text-[#1A1A1A]">
        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">1. About us</h2>
          <p>
            These Terms govern your use of <strong>AfriMarket</strong>, the online marketplace accessible at
            <a href="https://afrimarket.org" className="text-[#C05A3C] underline mx-1">afrimarket.org</a>
            and its associated mobile and progressive web applications (the &quot;Platform&quot;). The Platform
            is operated by <strong>AfriMarket Group Ltd</strong>, a private company limited by shares
            registered in England &amp; Wales under company number <strong>17282940</strong> (&quot;AfriMarket&quot;,
            &quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
          </p>
          <p>
            By creating an account, listing a product or service, making a purchase, or otherwise using the
            Platform, you agree to be bound by these Terms. If you do not agree, you must not use the Platform.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">2. The marketplace explained</h2>
          <p>
            AfriMarket connects buyers in the United Kingdom, United States, Canada and worldwide with
            sellers offering African and African-diaspora products and services. We are a <em>marketplace</em>,
            not the seller of the items listed. Each transaction is between you (the buyer) and an
            independent seller. AfriMarket facilitates discovery, communication and payment but is not a
            party to the underlying sale.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">3. Eligibility &amp; accounts</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>You must be at least 18 years old to create an account.</li>
            <li>You agree to provide accurate, current and complete information.</li>
            <li>You are responsible for keeping your password secure and for all activity on your account.</li>
            <li>You may not impersonate any person, create multiple accounts to evade restrictions, or use
              the Platform for unlawful purposes.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">4. Listings, fees &amp; commissions</h2>
          <p>
            Sellers may list products and services subject to our category rules and approval process. Some
            listings (e.g. weapons, restricted goods, counterfeits) are prohibited and will be removed.
          </p>
          <p>
            AfriMarket charges a platform commission of <strong>5%–10%</strong> on completed transactions
            (the exact rate depends on your seller tier — Founders pay 5%, standard sellers up to 10%).
            Additional optional fees may apply for premium services such as Verified badges or Pro Subscription
            (currently £4.99 / month). All fees are stated clearly before you confirm a transaction.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">5. Payments</h2>
          <p>
            Payments are processed by our third-party payment providers (currently Stripe and PayPal). By
            paying, you agree to their respective terms of service. AfriMarket does not store full payment
            card details on its servers. Currency conversion rates shown on the Platform are sourced from
            third-party FX providers and may differ slightly from your card-issuer rate at the time of
            settlement.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">6. Buyer protection &amp; disputes</h2>
          <p>
            AfriMarket offers a <Link to="/buyer-guarantee" className="text-[#C05A3C] underline">Buyer Guarantee</Link>{" "}
            for eligible orders and a Resolution Centre for disputes. If you encounter a problem with a
            seller, open a case through the Resolution Centre within 30 days of the issue. We will review
            the case and may issue refunds, suspend sellers, or take other action at our discretion.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">7. Seller obligations</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>You warrant that you have the right to sell every item or service listed.</li>
            <li>Descriptions and photos must be accurate and not misleading.</li>
            <li>You must respond to buyer enquiries within a reasonable time (typically 48 hours).</li>
            <li>You are responsible for your own tax obligations (income tax, VAT/sales tax where applicable).</li>
            <li>You agree to comply with all applicable laws, including consumer protection, product safety,
              and trade regulations.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">8. Prohibited conduct</h2>
          <p>You must not, and must not allow any third party to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Platform for fraudulent, illegal or harmful activities.</li>
            <li>Scrape, mirror or extract data without our written permission.</li>
            <li>Bypass any security or rate-limit features.</li>
            <li>Post abusive, discriminatory or hateful content.</li>
            <li>Sell counterfeit goods or items that infringe intellectual property rights.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">9. Intellectual property</h2>
          <p>
            All AfriMarket trademarks, logos, and platform design are owned by AfriMarket Group Ltd. You
            grant us a worldwide, royalty-free, non-exclusive licence to display the content you upload
            (listings, photos, reviews) for the purpose of operating and promoting the Platform.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">10. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, AfriMarket&apos;s aggregate liability arising out of or
            in connection with these Terms is limited to the greater of (a) the platform fees you paid us in
            the 12 months before the claim, or (b) £100. We are not liable for indirect or consequential
            losses, loss of profits, or damages caused by third-party sellers or buyers. Nothing in these
            Terms limits liability for death, personal injury caused by negligence, or fraud.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">11. Termination</h2>
          <p>
            You may close your account at any time from your account settings. We may suspend or terminate
            your account if you breach these Terms, if required by law, or if your activity poses a risk to
            other users.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">12. Governing law</h2>
          <p>
            These Terms are governed by the laws of England &amp; Wales. Any dispute is subject to the
            exclusive jurisdiction of the courts of England &amp; Wales, except where mandatory consumer
            protection law gives you a right to bring proceedings in your country of residence.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">13. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. The &quot;Last updated&quot; date at the top of this
            page reflects the latest version. Material changes will be communicated via the Platform or by
            email at least 14 days before they take effect.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">14. Contact</h2>
          <p>
            Questions about these Terms? Email{" "}
            <a href="mailto:admin@afrimarket.org" className="text-[#C05A3C] underline">admin@afrimarket.org</a>.
            We aim to respond within 3 business days.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            AfriMarket Group Ltd · Company No. 17282940 · Registered in England &amp; Wales.
          </p>
        </section>
      </div>
    </div>
  </div>
);

export default TermsOfService;
